interface InputFieldProps {
  id: string;
  label: string;
  icon: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  hint?: string;
  disabled?: boolean;
}

export function InputField({
  id,
  label,
  icon,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  hint,
  disabled,
}: InputFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground-400 mb-1.5">
        {label}
      </label>
      <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
        <div className="w-10 h-10 flex items-center justify-center shrink-0">
          <i className={`${icon} text-foreground-400`} />
        </div>
        <input
          id={id}
          name={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none disabled:opacity-50"
        />
      </div>
      {hint && <p className="mt-1 text-xs text-foreground-500">{hint}</p>}
    </div>
  );
}

interface TextareaFieldProps {
  id: string;
  label: string;
  icon: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  hint?: string;
}

export function TextareaField({
  id,
  label,
  icon,
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  hint,
}: TextareaFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground-400 mb-1.5">
        {label}
      </label>
      <div className="flex items-start bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
        <div className="w-10 h-10 flex items-center justify-center shrink-0 mt-1">
          <i className={`${icon} text-foreground-400`} />
        </div>
        <textarea
          id={id}
          name={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none resize-none"
        />
      </div>
      {hint && <p className="mt-1 text-xs text-foreground-500">{hint}</p>}
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  icon: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
  hint?: string;
}

export function SelectField({
  id,
  label,
  icon,
  value,
  onChange,
  options,
  placeholder,
  required,
  hint,
}: SelectFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-foreground-400 mb-1.5">
        {label}
      </label>
      <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
        <div className="w-10 h-10 flex items-center justify-center shrink-0">
          <i className={`${icon} text-foreground-400`} />
        </div>
        <select
          id={id}
          name={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          className="flex-1 bg-transparent text-sm text-foreground-50 py-2.5 pr-3 outline-none cursor-pointer"
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
      {hint && <p className="mt-1 text-xs text-foreground-500">{hint}</p>}
    </div>
  );
}